import { useEffect, useState } from "react";

/**
 * Animated typewriter for a textarea's `placeholder`: types a sample out, holds it,
 * erases it, then moves to the next one, looping forever.
 *
 * Static (no timers scheduled) when disabled, given fewer than two samples, or when
 * the client prefers reduced motion — in every static case this returns `samples[0]`
 * (or `""` if there is nothing to show).
 */

const TYPE_INTERVAL_MS = 45;
const HOLD_MS = 1800;
const ERASE_INTERVAL_MS = 25;
const BEAT_MS = 400;

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export const useTypewriterPlaceholder = (
  samples: string[],
  isEnabled: boolean
): string => {
  const [reducedMotion] = useState(prefersReducedMotion);
  const [text, setText] = useState("");

  const isStatic = !isEnabled || samples.length < 2 || reducedMotion;

  useEffect(() => {
    if (isStatic) return undefined;

    let timeoutId: ReturnType<typeof setTimeout>;
    let sampleIndex = 0;
    let charIndex = 0;

    const typeStep = () => {
      const sample = samples[sampleIndex % samples.length];

      charIndex += 1;
      setText(sample.slice(0, charIndex));

      timeoutId =
        charIndex >= sample.length
          ? setTimeout(eraseStep, HOLD_MS)
          : setTimeout(typeStep, TYPE_INTERVAL_MS);
    };

    const eraseStep = () => {
      const sample = samples[sampleIndex % samples.length];

      charIndex -= 1;
      setText(sample.slice(0, charIndex));

      if (charIndex <= 0) {
        sampleIndex += 1;
        timeoutId = setTimeout(typeStep, BEAT_MS);
      } else {
        timeoutId = setTimeout(eraseStep, ERASE_INTERVAL_MS);
      }
    };

    timeoutId = setTimeout(typeStep, TYPE_INTERVAL_MS);

    return () => clearTimeout(timeoutId);
    // Keyed on the samples' content, not the array's identity: callers build this
    // list from `formatMessage` on every render, so depending on `samples` itself
    // would tear the animation down and restart it from one character each time.
  }, [isStatic, samples.join("\n")]);

  if (isStatic) return samples[0] ?? "";

  return text;
};
